/** @jsxRuntime classic */
/** @jsx jsx */

import { useDraggable } from '@dnd-kit/core';
import { jsx } from '@emotion/react';
import React from 'react';
import { useMiddlewareContext } from '../../hooks/useMiddlewareContext';
import { IDraggableDayToDropProps } from '../../types';
import { DndIcon } from '../../utils/DndIcon';
import { ResizeIcon } from '../../utils/ResizeIcon';
import { isSameDate } from '../../utils/timeUtils';
import { draggableContainer, draggableContent, draggableHandler, draggableResizeHandler } from '../styles';
import { ResourceNameTypeEnum } from '../../../types/enums';
import { MIDNIGHT } from '../../../lib/timeUtils';
import { Resizable } from 'react-resizable';
import { getResizeConstraints, useResize } from './hooks/useResize';

export const Draggable: React.FC<IDraggableDayToDropProps> = ({
	children,
	id,
	state,
	parentId,
	groupTop,
	groupCount,
	groupIndex,
	isEditable,
	currentDate,
	setDraggableSlot,
	updateSlotData,
}) => {
	const elementId = `draggable-${id}` as const;

	const { options } = useMiddlewareContext();

	isEditable &&= options.draggingOptions?.[state.event?.parentType as ResourceNameTypeEnum];

	const { isDragging, setNodeRef, listeners } = useDraggable({
		id: String(id),
		data: {
			parentId: parentId,
			event: state,
		},
	});

	const { ref, onResize, onResizeEnd, size, top } = useResize({ state, currentDate, parentId, date: currentDate });

	const isResizable: boolean =
		Boolean(options.dragAndDrop) &&
		(options.resize?.includes(state.slotType) || false) &&
		isEditable! &&
		!isDragging &&
		(isSameDate(state.endDate, currentDate) || state.endTime === MIDNIGHT);

	return (
		<div
			css={draggableContainer(top - (groupTop || 0), groupCount, groupIndex)}
			id={elementId}
			ref={setNodeRef}
			data-aid="Draggable-daily"
		>
			<Resizable
				axis={isResizable ? 'y' : 'none'}
				onResize={(e, data) => onResize(e as any, data, setDraggableSlot)}
				onResizeStop={(e, data) => onResizeEnd(updateSlotData)}
				height={size}
				minConstraints={getResizeConstraints(options.dndStepLength)}
				handle={
					isResizable && (
						<div css={draggableResizeHandler('ns-resize')}>
							<ResizeIcon />
						</div>
					)
				}
			>
				<div data-aid={`draggable-content-${state.id}`} css={draggableContent(size)} ref={ref}>
					{children}
					{options.dragAndDrop && isEditable && (
						<div {...listeners} css={draggableHandler}>
							<DndIcon />
						</div>
					)}
				</div>
			</Resizable>
		</div>
	);
};
